<script setup>
import { onBeforeUnmount, onMounted, reactive, ref } from 'vue'
import { Icon } from '@iconify/vue'
import emitter from '../utils/emitter'

const notice = ref(null)
const box = ref(null)
const pos = reactive({ left: 0, top: 8, width: 520 })
let hideTimer = null
let resizeObserver = null
let mutationObserver = null

const titles = { success: 'Success', error: 'Error', warning: 'Warning', info: 'Info' }
const icons = {
  success: 'fa6-solid:circle-check',
  error: 'fa6-solid:circle-xmark',
  warning: 'fa6-solid:triangle-exclamation',
  info: 'fa6-solid:circle-info',
}

function findDialogContent() {
  const overlays = [...document.querySelectorAll('.v-overlay--active:not(.v-snackbar) .v-overlay__content')]
    .filter((el) => {
      if (!el.getClientRects().length) return false
      if (el.closest('.v-snackbar')) return false
      return true
    })
  return overlays.at(-1) || null
}

function place() {
  const el = findDialogContent()
  if (!el) return false
  const rect = el.getBoundingClientRect()
  const height = box.value?.offsetHeight || 48
  const maxWidth = Math.max(240, window.innerWidth - 16)
  pos.width = Math.min(Math.round(rect.width), maxWidth)
  pos.left = Math.round(rect.left)
  if (pos.left + pos.width > window.innerWidth - 8) {
    pos.left = Math.max(8, window.innerWidth - 8 - pos.width)
  }
  if (pos.left < 8) pos.left = 8
  pos.top = Math.max(8, Math.round(rect.top - height - 8))
  return true
}

function unwatch() {
  window.removeEventListener('resize', onLayout)
  window.removeEventListener('scroll', onLayout, true)
  resizeObserver?.disconnect()
  resizeObserver = null
  mutationObserver?.disconnect()
  mutationObserver = null
}

function onLayout() {
  if (!notice.value) return
  place()
}

function watchDialog() {
  unwatch()
  window.addEventListener('resize', onLayout)
  window.addEventListener('scroll', onLayout, true)
  const el = findDialogContent()
  if (el && typeof ResizeObserver !== 'undefined') {
    resizeObserver = new ResizeObserver(onLayout)
    resizeObserver.observe(el)
  }
  mutationObserver = new MutationObserver(onLayout)
  mutationObserver.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['class', 'style'] })
}

function hide() {
  if (hideTimer) {
    window.clearTimeout(hideTimer)
    hideTimer = null
  }
  notice.value = null
  unwatch()
}

function show(payload) {
  const next = typeof payload === 'string' ? { message: payload } : payload || {}
  const type = next.type || 'success'
  notice.value = {
    title: next.title || titles[type] || 'Info',
    message: next.message || '',
    type,
  }
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      place()
      watchDialog()
    })
  })
  if (hideTimer) window.clearTimeout(hideTimer)
  if (next.persistent) return
  hideTimer = window.setTimeout(hide, next.timeout ?? 2800)
}

onMounted(() => {
  emitter.on('dialog-notify', show)
})
onBeforeUnmount(() => {
  emitter.off('dialog-notify', show)
  hide()
})
</script>

<template>
  <Teleport to="body">
    <Transition name="dialog-notice">
      <div
        v-if="notice"
        ref="box"
        class="nr-notify dialog-notice-host"
        :class="'nr-notify--' + notice.type"
        role="status"
        :style="{ left: pos.left + 'px', top: pos.top + 'px', width: pos.width + 'px' }"
      >
        <div class="nr-notify__row">
          <span class="nr-notify__icon" aria-hidden="true">
            <Icon :icon="icons[notice.type] || icons.info" width="15" height="15" />
          </span>
          <div class="nr-notify__text">
            <div class="nr-notify__title">{{ notice.title }}</div>
            <div class="nr-notify__message">{{ notice.message }}</div>
          </div>
          <button type="button" class="nr-notify__close" aria-label="Dismiss" @click="hide">
            <Icon icon="fa6-solid:xmark" width="12" height="12" />
          </button>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>
